import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Database, Plus, Search, MessageCircle, Mail, Upload, Eye,
  ArrowRight, Ban, Trash2, X, Tag, ExternalLink,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { contactosApi } from '../../lib/api';

// ----- Catálogos -----

const ESTADOS = [
  { id: 'sin_seguimiento', label: 'Sin seguimiento', color: 'bg-slate-100 text-slate-700' },
  { id: 'en_tablero',      label: 'En tablero',      color: 'bg-blue-100 text-blue-700' },
  { id: 'ganado',          label: 'Ganado',          color: 'bg-emerald-100 text-emerald-700' },
  { id: 'perdido',         label: 'Perdido',         color: 'bg-rose-100 text-rose-700' },
  { id: 'descartado',      label: 'Descartado',      color: 'bg-slate-200 text-slate-600' },
  { id: 'no_contactar',    label: 'No contactar',    color: 'bg-amber-100 text-amber-700' },
];
const TIPOLOGIAS = ['Oficina', 'Local', 'Cowork', 'Vivienda', 'Cochera', 'Baulera'];
const PROYECTOS = ['Torre Emilia', 'Foresta', 'Senderos'];
const TIPOS_OP = ['Alquiler', 'Compra'];
const USOS_BUSCADOS = ['Quiere usar', 'Quiere renta', 'Ambos'];
const ORIGENES = ['WhatsApp', 'Instagram', 'Referido', 'Web', 'Cartel / visita espontánea', 'Inmobiliaria', 'Base vieja', 'Base propia', 'Otro'];

const estadoLabel = (id) => ESTADOS.find(e => e.id === id)?.label || id;
const estadoColor = (id) => ESTADOS.find(e => e.id === id)?.color || '';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const waLink = (whatsapp, contacto) => {
  const digits = (whatsapp || '').replace(/\D/g, '');
  if (!digits) return null;
  const nombre = (contacto?.nombre || '').split(' ')[0] || '';
  const tipologia = contacto?.tipologia || '';
  const proyecto = contacto?.proyecto_edificio || '';
  let msg = `Hola${nombre ? ' ' + nombre : ''}, ¿cómo estás? Te contacto desde Global Citizen`;
  if (tipologia || proyecto) {
    msg += ` por tu consulta sobre ${tipologia || 'tu interés'}${proyecto ? ' en ' + proyecto : ''}`;
  }
  msg += '.';
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
};

// ===== Componente raíz =====

const ComercialBaseDatos = () => {
  const navigate = useNavigate();
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  // Filtros
  const [search, setSearch] = useState('');
  const [fProyecto, setFProyecto] = useState('todos');
  const [fOperacion, setFOperacion] = useState('todos');
  const [fTipologia, setFTipologia] = useState('todos');
  const [fEstado, setFEstado] = useState('todos');

  // Diálogos
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { contacto, hasLead }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await contactosApi.list();
        if (alive) setContactos(res.data || []);
      } catch (e) {
        if (alive) toast.error('No se pudieron cargar los contactos');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return contactos.filter(c => {
      if (s) {
        const blob = `${c.nombre || ''} ${c.apellido || ''} ${c.empresa || ''} ${c.email || ''} ${c.whatsapp || ''}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      if (fProyecto !== 'todos' && c.proyecto_edificio !== fProyecto) return false;
      if (fOperacion !== 'todos' && c.tipo_operacion !== fOperacion) return false;
      if (fTipologia !== 'todos' && c.tipologia !== fTipologia) return false;
      if (fEstado !== 'todos' && c.estado !== fEstado) return false;
      return true;
    });
  }, [contactos, search, fProyecto, fOperacion, fTipologia, fEstado]);

  const handleActivar = async (contactoId) => {
    const c = contactos.find(x => x.contacto_id === contactoId);
    if (c?.lead_id_activo) {
      // Ya está en tablero — solo navegar
      navigate('/comercial/leads');
      return;
    }
    try {
      await contactosApi.activarTablero(contactoId);
      toast.success('Activado en tablero');
      reload();
      navigate('/comercial/leads');
    } catch (e) {
      toast.error('No se pudo activar en tablero');
    }
  };

  const handleNoContactar = async (contactoId) => {
    if (!window.confirm('¿Marcar este contacto como "No contactar"?')) return;
    try {
      await contactosApi.noContactar(contactoId);
      reload();
    } catch (e) {
      toast.error('No se pudo marcar');
    }
  };

  const handleDelete = (contacto) => {
    setConfirmDelete({ contacto, hasLead: !!contacto.lead_id_activo, force: false });
  };

  const handleConfirmDelete = async (force) => {
    if (!confirmDelete) return;
    const { contacto } = confirmDelete;
    try {
      await contactosApi.remove(contacto.contacto_id, force);
      toast.success(force ? 'Contacto y lead eliminados' : 'Contacto eliminado');
      setConfirmDelete(null);
      if (selectedId === contacto.contacto_id) setSelectedId(null);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === 'has_active_lead') {
        setConfirmDelete({ contacto, hasLead: true, lead: detail.lead });
      } else {
        toast.error(detail || 'No se pudo eliminar');
      }
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="comercial-base-datos-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-slate-100">
          <Database className="w-5 h-5 text-slate-700" />
        </div>
        <h1 className="text-2xl font-semibold text-slate-900">Base de Datos</h1>
      </div>

      {/* Barra de filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar nombre, empresa, email o WhatsApp…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="bd-search-input"
          />
        </div>
        <FilterSelect value={fProyecto} onChange={setFProyecto} shortLabel="Proyecto"
          options={[{ value: 'todos', label: 'Todos' }, ...PROYECTOS.map(p => ({ value: p, label: p }))]}
          testid="bd-filter-proyecto" />
        <FilterSelect value={fOperacion} onChange={setFOperacion} shortLabel="Operación"
          options={[{ value: 'todos', label: 'Todas' }, ...TIPOS_OP.map(p => ({ value: p, label: p }))]}
          testid="bd-filter-operacion" />
        <FilterSelect value={fTipologia} onChange={setFTipologia} shortLabel="Tipología"
          options={[{ value: 'todos', label: 'Todas' }, ...TIPOLOGIAS.map(p => ({ value: p, label: p }))]}
          testid="bd-filter-tipologia" />
        <FilterSelect value={fEstado} onChange={setFEstado} shortLabel="Estado"
          options={[{ value: 'todos', label: 'Todos' }, ...ESTADOS.map(e => ({ value: e.id, label: e.label }))]}
          testid="bd-filter-estado" />

        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="bd-import-btn">
            <Upload className="w-4 h-4 mr-1" /> Importar
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bd-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo contacto
          </Button>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center text-slate-500 py-12">Cargando…</div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bd-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-600 text-xs uppercase">
                  <th className="px-3 py-2 font-semibold">Nombre</th>
                  <th className="px-3 py-2 font-semibold">Empresa</th>
                  <th className="px-3 py-2 font-semibold">WhatsApp</th>
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold">Tipología</th>
                  <th className="px-3 py-2 font-semibold">Proyecto</th>
                  <th className="px-3 py-2 font-semibold">Operación</th>
                  <th className="px-3 py-2 font-semibold">Estado</th>
                  <th className="px-3 py-2 font-semibold">Últ. contacto</th>
                  <th className="px-3 py-2 font-semibold">Próx. contacto</th>
                  <th className="px-3 py-2 font-semibold">Origen</th>
                  <th className="px-3 py-2 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={12} className="text-center text-slate-400 py-10">
                    {contactos.length === 0 ? 'No hay contactos cargados. Comenzá creando uno o importando una base.' : 'Sin resultados con los filtros aplicados'}
                  </td></tr>
                ) : (
                  filtered.map(c => (
                    <ContactoRow
                      key={c.contacto_id}
                      c={c}
                      onClick={() => setSelectedId(c.contacto_id)}
                      onActivar={() => handleActivar(c.contacto_id)}
                      onNoContactar={() => handleNoContactar(c.contacto_id)}
                      onDelete={() => handleDelete(c)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-100">
            {filtered.length} de {contactos.length} contactos
          </div>
        </Card>
      )}

      {selectedId && (
        <ContactoDrawer
          contactoId={selectedId}
          onOpenChange={(v) => { if (!v) setSelectedId(null); }}
          onChanged={reload}
          onActivar={() => handleActivar(selectedId)}
          onNoContactar={() => handleNoContactar(selectedId)}
          onDelete={(c) => handleDelete(c)}
        />
      )}

      {showNew && (
        <NewContactoDialog open={showNew} onOpenChange={setShowNew} onCreated={reload} />
      )}

      {showImport && (
        <ImportDialog open={showImport} onOpenChange={setShowImport} onApplied={reload} />
      )}

      {confirmDelete && (
        <DeleteConfirmDialog
          state={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

// ===== Row de tabla =====

const ContactoRow = ({ c, onClick, onActivar, onNoContactar, onDelete }) => {
  const wa = waLink(c.whatsapp, c);
  const enTablero = !!c.lead_id_activo;
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`bd-row-${c.contacto_id}`}>
      <td className="px-3 py-2 font-medium text-slate-900" onClick={onClick}>
        {c.nombre} {c.apellido}
      </td>
      <td className="px-3 py-2 text-slate-700" onClick={onClick}>{c.empresa || '—'}</td>
      <td className="px-3 py-2 text-slate-700" onClick={onClick}>{c.whatsapp || '—'}</td>
      <td className="px-3 py-2 text-slate-700 max-w-[180px] truncate" onClick={onClick}>{c.email || '—'}</td>
      <td className="px-3 py-2 text-slate-700" onClick={onClick}>{c.tipologia || '—'}</td>
      <td className="px-3 py-2 text-slate-700" onClick={onClick}>{c.proyecto_edificio || '—'}</td>
      <td className="px-3 py-2 text-slate-700" onClick={onClick}>{c.tipo_operacion || '—'}</td>
      <td className="px-3 py-2" onClick={onClick}>
        <Badge className={`${estadoColor(c.estado)} border-0`}>{estadoLabel(c.estado)}</Badge>
      </td>
      <td className="px-3 py-2 text-slate-600 text-xs" onClick={onClick}>{fmtDate(c.fecha_ultimo_contacto)}</td>
      <td className="px-3 py-2 text-slate-600 text-xs" onClick={onClick}>{fmtDate(c.proximo_contacto)}</td>
      <td className="px-3 py-2 text-slate-600 text-xs" onClick={onClick}>{c.origen || '—'}</td>
      <td className="px-3 py-2 text-right">
        <div className="flex gap-1 justify-end items-center">
          {wa && (
            <a href={wa} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="Abrir WhatsApp" data-testid={`bd-wa-${c.contacto_id}`}>
              <MessageCircle className="w-4 h-4" />
            </a>
          )}
          <button onClick={(e) => { e.stopPropagation(); onClick(); }}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Ver/editar" data-testid={`bd-view-${c.contacto_id}`}>
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onActivar(); }}
            disabled={c.estado === 'no_contactar'}
            className={`rounded text-xs px-2 py-1 ${enTablero ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'hover:bg-emerald-50 text-emerald-700 disabled:opacity-30'}`}
            title={enTablero ? 'Ver lead en tablero' : 'Activar en tablero'}
            data-testid={`bd-activar-${c.contacto_id}`}
          >
            {enTablero ? 'Ver lead' : 'Activar'}
          </button>
          {c.estado !== 'no_contactar' && (
            <button onClick={(e) => { e.stopPropagation(); onNoContactar(); }}
              className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Marcar no contactar" data-testid={`bd-nc-${c.contacto_id}`}>
              <Ban className="w-4 h-4" />
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Eliminar contacto" data-testid={`bd-delete-${c.contacto_id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ===== Drawer detalle =====

const ContactoDrawer = ({ contactoId, onOpenChange, onChanged, onActivar, onNoContactar, onDelete }) => {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await contactosApi.get(contactoId);
        if (!alive) return;
        setC(res.data); setForm(res.data);
      } catch (_) {
        if (alive) toast.error('No se pudo cargar el contacto');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [contactoId, reloadKey]);

  const save = async () => {
    try {
      await contactosApi.update(contactoId, {
        nombre: form.nombre, apellido: form.apellido, empresa: form.empresa,
        whatsapp: form.whatsapp, email: form.email,
        tipo_operacion: form.tipo_operacion, uso_buscado: form.uso_buscado,
        tipologia: form.tipologia, proyecto_edificio: form.proyecto_edificio,
        origen: form.origen, estado: form.estado,
        notas: form.notas, tags: form.tags, proximo_contacto: form.proximo_contacto,
      });
      setEditing(false);
      setReloadKey(k => k + 1);
      onChanged();
    } catch (e) {
      toast.error('No se pudo actualizar');
    }
  };

  const handleDelete = () => {
    onDelete && onDelete(c);
  };

  const wa = c && waLink(c.whatsapp, c);

  return (
    <Sheet open={!!contactoId} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="bd-drawer">
        <SheetHeader>
          <SheetTitle>{c ? `${c.nombre || ''} ${c.apellido || ''}`.trim() || 'Contacto' : 'Contacto'}</SheetTitle>
        </SheetHeader>

        {loading || !c ? (
          <div className="text-center text-slate-500 py-12">Cargando…</div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${estadoColor(c.estado)} border-0`}>{estadoLabel(c.estado)}</Badge>
              {c.tags?.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
              {c.fuente_carga === 'import' && (
                <Badge variant="outline" className="text-xs">
                  <Upload className="w-3 h-3 mr-1" /> Importado: {c.import_archivo}
                </Badge>
              )}
            </div>

            {/* Acciones */}
            <Card className="p-3 flex flex-wrap gap-2">
              {wa && (
                <a href={wa} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
                  data-testid="bd-drawer-wa-btn">
                  <MessageCircle className="w-3.5 h-3.5" /> Abrir WhatsApp
                </a>
              )}
              <Button size="sm" variant="outline" onClick={onActivar} disabled={c.estado === 'no_contactar'} data-testid="bd-drawer-activar-btn">
                <ArrowRight className="w-3.5 h-3.5 mr-1" />
                {c.lead_id_activo ? 'Ver lead en tablero' : 'Activar en tablero'}
              </Button>
              {c.estado !== 'no_contactar' && (
                <Button size="sm" variant="outline" onClick={onNoContactar} className="text-amber-600 border-amber-300">
                  <Ban className="w-3.5 h-3.5 mr-1" /> No contactar
                </Button>
              )}
            </Card>

            {/* Datos */}
            {!editing ? (
              <Card className="p-3 space-y-1.5 text-sm">
                <Row label="Empresa">{c.empresa || '—'}</Row>
                <Row label="WhatsApp">{c.whatsapp || '—'} {c.whatsapp_norm && <span className="text-xs text-slate-400">({c.whatsapp_norm})</span>}</Row>
                <Row label="Email">{c.email || '—'}</Row>
                <Row label="Operación">{[c.tipo_operacion, c.uso_buscado].filter(Boolean).join(' · ') || '—'}</Row>
                <Row label="Tipología">{c.tipologia || '—'}</Row>
                <Row label="Proyecto">{c.proyecto_edificio || '—'}</Row>
                <Row label="Origen">{c.origen || '—'}</Row>
                <Row label="Próximo contacto">{fmtDate(c.proximo_contacto)}</Row>
                <Row label="Último contacto">{fmtDate(c.fecha_ultimo_contacto)}</Row>
                {c.notas && (
                  <div className="pt-2 mt-2 border-t border-slate-100">
                    <Label className="text-xs text-slate-500">Notas</Label>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.notas}</p>
                  </div>
                )}
                <div className="pt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="bd-drawer-edit-btn">Editar</Button>
                  <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={handleDelete}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                  </Button>
                </div>
              </Card>
            ) : (
              <Card className="p-3 grid grid-cols-2 gap-2">
                <Field label="Nombre"><Input value={form.nombre || ''} onChange={(e) => setForm(s => ({ ...s, nombre: e.target.value }))} /></Field>
                <Field label="Apellido"><Input value={form.apellido || ''} onChange={(e) => setForm(s => ({ ...s, apellido: e.target.value }))} /></Field>
                <Field label="Empresa"><Input value={form.empresa || ''} onChange={(e) => setForm(s => ({ ...s, empresa: e.target.value }))} /></Field>
                <Field label="WhatsApp"><Input value={form.whatsapp || ''} onChange={(e) => setForm(s => ({ ...s, whatsapp: e.target.value }))} /></Field>
                <Field label="Email"><Input value={form.email || ''} onChange={(e) => setForm(s => ({ ...s, email: e.target.value }))} /></Field>
                <Field label="Operación">
                  <SimpleSelect value={form.tipo_operacion || ''} onChange={(v) => setForm(s => ({ ...s, tipo_operacion: v, uso_buscado: v === 'Compra' ? s.uso_buscado : '' }))} options={TIPOS_OP} />
                </Field>
                {form.tipo_operacion === 'Compra' && (
                  <Field label="Uso buscado">
                    <SimpleSelect value={form.uso_buscado || ''} onChange={(v) => setForm(s => ({ ...s, uso_buscado: v }))} options={USOS_BUSCADOS} />
                  </Field>
                )}
                <Field label="Tipología">
                  <SimpleSelect value={form.tipologia || ''} onChange={(v) => setForm(s => ({ ...s, tipologia: v }))} options={TIPOLOGIAS} />
                </Field>
                <Field label="Proyecto">
                  <SimpleSelect value={form.proyecto_edificio || ''} onChange={(v) => setForm(s => ({ ...s, proyecto_edificio: v }))} options={PROYECTOS} />
                </Field>
                <Field label="Origen">
                  <SimpleSelect value={form.origen || ''} onChange={(v) => setForm(s => ({ ...s, origen: v }))} options={ORIGENES} />
                </Field>
                <Field label="Estado">
                  <SimpleSelect value={form.estado || 'sin_seguimiento'} onChange={(v) => setForm(s => ({ ...s, estado: v }))}
                    options={ESTADOS.map(e => ({ value: e.id, label: e.label }))} />
                </Field>
                <Field label="Próximo contacto">
                  <Input type="date" value={form.proximo_contacto ? form.proximo_contacto.slice(0, 10) : ''}
                    onChange={(e) => setForm(s => ({ ...s, proximo_contacto: e.target.value }))} />
                </Field>
                <div className="col-span-2">
                  <Field label="Notas"><Textarea value={form.notas || ''} onChange={(e) => setForm(s => ({ ...s, notas: e.target.value }))} rows={3} /></Field>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(false); setForm(c); }}>Cancelar</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="bd-drawer-save-btn">Guardar</Button>
                </div>
              </Card>
            )}

            {/* Historial */}
            <Card className="p-3">
              <Label className="text-xs text-slate-500 mb-2 block">Historial</Label>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {(c.actividades || []).length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-4">Sin movimientos</div>
                ) : (
                  c.actividades.map(a => (
                    <div key={a.activity_id} className="text-xs border-l-2 border-slate-200 pl-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700 capitalize">{a.tipo.replace('_', ' ')}</span>
                        <span className="text-slate-400">{new Date(a.fecha).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className="text-slate-600">{a.descripcion}</div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ===== Nuevo contacto =====

const NewContactoDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    nombre: '', apellido: '', empresa: '', whatsapp: '', email: '',
    tipo_operacion: '', uso_buscado: '', tipologia: '', proyecto_edificio: '',
    origen: '', notas: '',
  });
  const [saving, setSaving] = useState(false);
  const [dupAlert, setDupAlert] = useState(null);

  const change = (k, v) => setForm(s => {
    const next = { ...s, [k]: v };
    if (k === 'tipo_operacion' && v !== 'Compra') next.uso_buscado = '';
    return next;
  });

  const submit = async (confirmDup = false) => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      setSaving(true);
      await contactosApi.create({ ...form, confirm_duplicate: confirmDup });
      toast.success('Contacto creado');
      onCreated();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === 'duplicate') {
        setDupAlert(detail.existing);
      } else {
        toast.error(detail || 'No se pudo crear el contacto');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bd-new-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo contacto</DialogTitle>
        </DialogHeader>

        {dupAlert && (
          <Card className="p-3 bg-amber-50 border-amber-300">
            <div className="text-sm text-amber-900 font-medium mb-1">⚠ Posible duplicado encontrado</div>
            <div className="text-xs text-amber-800">
              Ya existe un contacto con ese WhatsApp o email:<br />
              <strong>{dupAlert.nombre} {dupAlert.apellido}</strong>
              {dupAlert.empresa && ` · ${dupAlert.empresa}`}
              {dupAlert.whatsapp && ` · ${dupAlert.whatsapp}`}
              {dupAlert.email && ` · ${dupAlert.email}`}
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => setDupAlert(null)}>Cancelar</Button>
              <Button size="sm" onClick={() => submit(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bd-new-dup-create">
                Crear igual
              </Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombre *"><Input value={form.nombre} onChange={(e) => change('nombre', e.target.value)} data-testid="bd-new-nombre" /></Field>
          <Field label="Apellido"><Input value={form.apellido} onChange={(e) => change('apellido', e.target.value)} data-testid="bd-new-apellido" /></Field>
          <Field label="Empresa"><Input value={form.empresa} onChange={(e) => change('empresa', e.target.value)} /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => change('whatsapp', e.target.value)} placeholder="+54 9 261..." data-testid="bd-new-whatsapp" /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => change('email', e.target.value)} /></Field>
          <Field label="Operación"><SimpleSelect value={form.tipo_operacion} onChange={(v) => change('tipo_operacion', v)} options={TIPOS_OP} /></Field>
          {form.tipo_operacion === 'Compra' && (
            <Field label="Uso buscado"><SimpleSelect value={form.uso_buscado} onChange={(v) => change('uso_buscado', v)} options={USOS_BUSCADOS} /></Field>
          )}
          <Field label="Tipología"><SimpleSelect value={form.tipologia} onChange={(v) => change('tipologia', v)} options={TIPOLOGIAS} /></Field>
          <Field label="Proyecto"><SimpleSelect value={form.proyecto_edificio} onChange={(v) => change('proyecto_edificio', v)} options={PROYECTOS} /></Field>
          <Field label="Origen"><SimpleSelect value={form.origen} onChange={(v) => change('origen', v)} options={ORIGENES} /></Field>
          <div className="col-span-2">
            <Field label="Notas"><Textarea value={form.notas} onChange={(e) => change('notas', e.target.value)} rows={2} /></Field>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => submit(false)} disabled={saving || dupAlert} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bd-new-submit">
            {saving ? 'Guardando…' : 'Crear contacto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== Import dialog =====

const IMPORTABLE_FIELDS = [
  { key: 'nombre',   label: 'Nombre *' },
  { key: 'apellido', label: 'Apellido' },
  { key: 'empresa',  label: 'Empresa' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email',    label: 'Email' },
  { key: 'tipologia', label: 'Tipología' },
  { key: 'proyecto_edificio', label: 'Proyecto' },
  { key: 'tipo_operacion', label: 'Operación' },
  { key: 'origen',   label: 'Origen' },
  { key: 'notas',    label: 'Notas' },
];

const ImportDialog = ({ open, onOpenChange, onApplied }) => {
  const [step, setStep] = useState(1); // 1 upload, 2 preview/mapping, 3 result
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);  // {headers, rows, mapping_sugerido, total_rows}
  const [mapping, setMapping] = useState({});  // {field: header}
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      const res = await contactosApi.importParse(file);
      setParsed(res.data);
      setMapping(res.data.mapping_sugerido || {});
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo leer el archivo');
    } finally {
      setLoading(false);
    }
  };

  // Preview rows con mapping aplicado
  const previewRows = useMemo(() => {
    if (!parsed) return [];
    return parsed.rows.map(r => {
      const mapped = {};
      for (const { key } of IMPORTABLE_FIELDS) {
        const header = mapping[key];
        mapped[key] = header && r[header] ? String(r[header]).trim() : '';
      }
      return mapped;
    });
  }, [parsed, mapping]);

  // Stats locales
  const localStats = useMemo(() => {
    const validas = previewRows.filter(r => r.nombre);
    const sinNombre = previewRows.length - validas.length;
    return { total: previewRows.length, sin_nombre: sinNombre, listas: validas.length };
  }, [previewRows]);

  const handleApply = async () => {
    const validas = previewRows.filter(r => r.nombre);
    if (validas.length === 0) { toast.error('No hay filas válidas para importar'); return; }
    try {
      setLoading(true);
      const res = await contactosApi.importApply({
        archivo_nombre: parsed.archivo_nombre,
        rows: validas,
        modo: 'upsert',
      });
      setResult(res.data);
      setStep(3);
      onApplied();
    } catch (e) {
      toast.error('No se pudo aplicar la importación');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setParsed(null); setMapping({}); setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bd-import-dialog">
        <DialogHeader>
          <DialogTitle>Importar base de contactos</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Subí un archivo <strong>.xlsx</strong> o <strong>.csv</strong>. La primera fila debe contener los nombres de columnas.
              Nada se guardará hasta que confirmes en el paso 3.
            </p>
            <Input
              type="file"
              accept=".xlsx,.xlsm,.csv,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="bd-import-file"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={handleUpload} disabled={!file || loading} data-testid="bd-import-upload-btn">
                {loading ? 'Procesando…' : 'Subir y previsualizar'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && parsed && (
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              Archivo: <strong>{parsed.archivo_nombre}</strong> · {parsed.total_rows} filas detectadas
              {parsed.truncated && <span className="text-amber-600"> · mostrando primeras 500</span>}
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Mapeo de columnas</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {IMPORTABLE_FIELDS.map(f => (
                  <div key={f.key} className="flex items-center gap-2">
                    <Label className="text-xs w-28 text-slate-600">{f.label}</Label>
                    <Select value={mapping[f.key] || '__none__'} onValueChange={(v) => setMapping(m => ({ ...m, [f.key]: v === '__none__' ? '' : v }))}>
                      <SelectTrigger className="h-8 flex-1" data-testid={`bd-import-map-${f.key}`}>
                        <SelectValue>
                          {mapping[f.key] || <span className="text-slate-400 italic">— sin asignar —</span>}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— sin asignar —</SelectItem>
                        {parsed.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-sm flex gap-4">
              <span><strong className="text-emerald-600">{localStats.listas}</strong> listas para importar</span>
              {localStats.sin_nombre > 0 && <span><strong className="text-rose-600">{localStats.sin_nombre}</strong> sin nombre (descartadas)</span>}
              <span className="text-slate-500">Total: {localStats.total}</span>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Previsualización (primeras 8 filas)</div>
              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      {IMPORTABLE_FIELDS.map(f => <th key={f.key} className="px-2 py-1 text-left font-semibold text-slate-600">{f.label.replace(' *','')}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(0, 8).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {IMPORTABLE_FIELDS.map(f => (
                          <td key={f.key} className="px-2 py-1 text-slate-700 max-w-[120px] truncate">
                            {r[f.key] || <span className="text-slate-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
              Los contactos con WhatsApp o email duplicado actualizarán al existente (sólo se completan campos vacíos).
              No se borra ningún contacto ya cargado.
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={reset}>← Volver</Button>
              <Button onClick={handleApply} disabled={loading || localStats.listas === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bd-import-apply-btn">
                {loading ? 'Importando…' : `Importar ${localStats.listas} contactos`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-3">
            <div className="text-sm text-slate-700 font-medium">Importación completada</div>
            <div className="grid grid-cols-3 gap-2">
              <Card className="p-3 text-center">
                <div className="text-xs text-slate-500">Nuevos</div>
                <div className="text-2xl font-semibold text-emerald-600">{result.nuevos}</div>
              </Card>
              <Card className="p-3 text-center">
                <div className="text-xs text-slate-500">Actualizados</div>
                <div className="text-2xl font-semibold text-blue-600">{result.actualizados}</div>
              </Card>
              <Card className="p-3 text-center">
                <div className="text-xs text-slate-500">Errores</div>
                <div className="text-2xl font-semibold text-rose-600">{result.errores}</div>
              </Card>
            </div>
            {result.errores_detalle?.length > 0 && (
              <div className="text-xs text-slate-600 max-h-32 overflow-y-auto bg-slate-50 p-2 rounded">
                <strong>Errores:</strong>
                {result.errores_detalle.slice(0, 10).map((e, i) => (
                  <div key={i}>Fila {e.row}: {e.motivo}</div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ===== Diálogo confirmación de eliminación =====

const DeleteConfirmDialog = ({ state, onCancel, onConfirm }) => {
  const { contacto, hasLead, lead } = state;
  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="bd-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700">Eliminar contacto</DialogTitle>
        </DialogHeader>
        {!hasLead ? (
          <>
            <p className="text-sm text-slate-700">
              ¿Seguro querés eliminar a <strong>{contacto.nombre} {contacto.apellido}</strong>?
              Esta acción no se puede deshacer.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onCancel}>Cancelar</Button>
              <Button onClick={() => onConfirm(false)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="bd-delete-confirm">
                Eliminar
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
              ⚠ Este contacto tiene un <strong>lead activo</strong> en el tablero
              {lead?.estado && <> (etapa: <strong>{lead.estado}</strong>)</>}.
              Si lo eliminás, también se va a borrar el lead del tablero.
            </div>
            <p className="text-sm text-slate-700">
              ¿Querés eliminar <strong>{contacto.nombre} {contacto.apellido}</strong> y su lead vinculado?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onCancel}>Cancelar</Button>
              <Button onClick={() => onConfirm(true)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="bd-delete-confirm-force">
                Eliminar contacto y lead
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ===== Helpers UI =====

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex items-start gap-2 text-sm">
    <div className="text-[10px] text-slate-400 uppercase w-28 mt-0.5 flex-shrink-0">{label}</div>
    <div className="text-slate-800 flex-1 min-w-0">{children}</div>
  </div>
);

const SimpleSelect = ({ value, onChange, options, testid, disabled }) => {
  const opts = options.map(o => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9" data-testid={testid}>
        <SelectValue placeholder="Seleccionar…" />
      </SelectTrigger>
      <SelectContent>
        {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const FilterSelect = ({ value, onChange, options, testid, shortLabel }) => {
  const isAll = value === 'todos';
  const current = options.find(o => o.value === value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[130px]" data-testid={testid}>
        <SelectValue>
          <span className={isAll ? 'text-slate-500' : 'text-slate-900'}>
            {isAll ? shortLabel : (current?.label || shortLabel)}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export default ComercialBaseDatos;
